import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import Card from "./common/Card";
import Alert from "./common/Alert";
import Icon from "./common/Icon";
import { useAuthInfo } from "../context/AuthInfoContext";
import { useToken } from "../hooks/useToken";
import { clearAuthSessionState } from "../utils/authRedirect";
import { startActivation } from "../features/access/services/activationService";
import { firstLanding, pendingActivations } from "../utils/landing";
import "../pages/diagnostics/systemPages.css";

function messageOf(e) {
  const msg = e?.responseBody?.error?.message;
  return typeof msg === "string" && msg.trim() ? msg : e?.message || "Activeren is niet gelukt.";
}

// Waar landen na het activeren: de toestand van auth/me alsof de rol al loopt (de echte auth/me laadt stil op de
// achtergrond), met voorrang voor de werkpagina van die rol (Beheer → Toegang).
function landingAfter(info, role, expiresAt) {
  const until = expiresAt || new Date(Date.now() + role.maxHours * 3600000).toISOString();
  const roles = Array.isArray(info?.access?.roles) ? info.access.roles : [];
  const access = {
    ...info.access,
    hasActiveRole: true,
    roles: roles.map((r) => (r.idAssignment === role.idAssignment ? { ...r, active: true, activationExpiresAt: until } : r)),
  };
  return firstLanding({ ...info, access }, { prefer: [role.code] });
}

// Eén activeerbare rol: uren (1..max, standaard het maximum) en een optionele reden.
function ActivationForm({ role, info, tokenProvider, refresh }) {
  const navigate = useNavigate();
  const [hours, setHours] = useState(role.maxHours);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const id = `sp-activate-${role.idAssignment}`;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // Step-up (401 insufficient_claims) vangt apiClient zelf op: nieuw token met de claims en de actie opnieuw.
      const res = await startActivation(tokenProvider, { idAssignment: role.idAssignment, hours, reason });
      const target = landingAfter(info, role, res?.expiresAt);
      refresh?.({ silent: true });
      navigate(target?.path || "/", { replace: true });
    } catch (err) {
      setError(messageOf(err));
      setBusy(false);
    }
  };

  return (
    <form className="sp-activate" onSubmit={submit} aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`} className="sp-activate__title">
        {role.name}
      </h2>
      {error ? (
        <Alert type="error" title="Niet geactiveerd">
          {error}
        </Alert>
      ) : null}
      <label className="sp-field" htmlFor={`${id}-hours`}>
        Hoe lang
        <select id={`${id}-hours`} className="input" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
          {Array.from({ length: role.maxHours }, (_, i) => i + 1).map((h) => (
            <option key={h} value={h}>
              {h} uur
            </option>
          ))}
        </select>
      </label>
      <label className="sp-field" htmlFor={`${id}-reason`}>
        Reden (optioneel)
        <textarea
          id={`${id}-reason`}
          className="input"
          rows={2}
          maxLength={400}
          placeholder="optioneel"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <button type="submit" className="btn-primary-green sp-btn" disabled={busy}>
        <Icon name="shield" size={14} /> {busy ? "Bezig…" : "Activeren"}
      </button>
    </form>
  );
}

// Issue #23 (landing): aangemeld met enkel activeerbare rollen die nu niet lopen (bv. alleen Beheer). Zonder activatie
// telt geen enkele rol, dus elke pagina gaf 403 → "Geen toegang" → lus. Hier activeert de gebruiker de rol (max. 8 uur,
// reden optioneel, in de historiek) en gaat daarna meteen naar de eerste pagina die hij mag zien.
export default function ActivationScreen() {
  const info = useAuthInfo();
  const { access, refresh, user } = info;
  const { instance, accounts } = useMsal();
  const tokenProvider = useToken();
  const roles = pendingActivations(access);
  const account = instance?.getActiveAccount?.() || accounts?.[0];
  const name = account?.name || user?.name || "";

  const logout = () => {
    clearAuthSessionState();
    instance?.logoutRedirect?.();
  };

  return (
    <main className="sp-page sp-page--inline">
      <Card className="sp-card">
        <span className="sp-wait-icon" aria-hidden="true">
          <Icon name="shield" size={24} />
        </span>
        <h1 className="sp-title">{roles.length === 1 ? `${roles[0].name} activeren` : "Rol activeren"}</h1>
        <p className="sp-text">
          {name ? (
            <>
              Je bent aangemeld als <strong>{name}</strong>.{" "}
            </>
          ) : null}
          Je rechten tellen pas als je de rol activeert. Een activatie geldt hoogstens{" "}
          {roles.length > 0 ? Math.max(...roles.map((r) => r.maxHours)) : 8} uur; een reden is optioneel en komt in de historiek. Mogelijk vraagt
          de app een extra bevestiging van je aanmelding.
        </p>
        {roles.length === 0 ? (
          <Alert type="info">Er is geen rol meer om te activeren.</Alert>
        ) : (
          roles.map((r) => <ActivationForm key={r.idAssignment} role={r} info={info} tokenProvider={tokenProvider} refresh={refresh} />)
        )}
        <div className="sp-actions">
          <button type="button" className="btn-outline-green sp-btn" onClick={logout}>
            Afmelden en ander account
          </button>
        </div>
      </Card>
    </main>
  );
}
